import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { useState } from 'react';
import type { AvailableFieldsResponse } from '../engage-segments-tab/types';
import styles from './EditSegment.module.css';
import { RecipientsPreview } from './RecipientsPreview';
import { SegmentDefinitionEditor } from './SegmentDefinitionEditor';
import { EditorModeToggle } from './visual-builder/EditorModeToggle';
import { SegmentDescriptionView } from './visual-builder/SegmentDescriptionView';
import { SegmentSchemaCatalogProvider } from './visual-builder/SegmentSchemaCatalogProvider';
import { SegmentVisualBuilder } from './visual-builder/SegmentVisualBuilder';

export interface SegmentDefinitionSectionProps {
  formatType: 'legacy' | 'new';
  editorMode: 'builder' | 'json' | 'preview';
  onEditorModeChange: (mode: 'builder' | 'json' | 'preview') => void;
  contentsJson: string;
  onContentsChange: (json: string) => void;
  onValidationChange: (valid: boolean) => void;
  isJsonValid: boolean;
  isSaving: boolean;
  availableFields: AvailableFieldsResponse;
  isLoadingFields: boolean;
  fieldsError: Error | null;
  parsedConditions: BusinessSegmentConditions | null;
  parsedConditionGroups: SegmentConditionGroups | null;
  conditionGroupsForPreview: SegmentConditionGroups | null;
}

export function SegmentDefinitionSection({
  formatType,
  editorMode,
  onEditorModeChange,
  contentsJson,
  onContentsChange,
  onValidationChange,
  isJsonValid,
  isSaving,
  availableFields,
  isLoadingFields,
  fieldsError,
  parsedConditions,
  parsedConditionGroups,
  conditionGroupsForPreview,
}: SegmentDefinitionSectionProps) {
  const [jsonEditorNotice, setJsonEditorNotice] = useState<string | null>(null);
  const editorDescriptionByMode = {
    builder: 'Build your segment with the visual builder',
    json: 'Define your segment using JSON condition groups',
    preview: 'Read your segment as a description outline',
  } as const;
  const editorDescription =
    formatType === 'legacy'
      ? 'Define your segment using JSON conditions'
      : editorDescriptionByMode[editorMode];
  const handleEditorModeChange = (mode: 'builder' | 'json' | 'preview') => {
    if (mode !== 'json') {
      setJsonEditorNotice(null);
    }
    onEditorModeChange(mode);
  };
  const showUnsupportedSegmentInJsonEditor = (reason: string) => {
    setJsonEditorNotice(reason);
    onEditorModeChange('json');
  };
  const handleContentsChange = (json: string) => {
    setJsonEditorNotice(null);
    onContentsChange(json);
  };

  return (
    <section className={styles.columns}>
      <article className={styles.editor} id="segment-editor-panel">
        <header>
          <h2>Segment Definition</h2>
          <p>{editorDescription}</p>
          {formatType === 'new' && (
            <EditorModeToggle mode={editorMode} onChange={handleEditorModeChange} disabled={isSaving} />
          )}
        </header>
        {formatType === 'legacy' ? (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={onContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames
            hideHeader
          />
        ) : editorMode === 'builder' ? (
          <SegmentSchemaCatalogProvider>
            <SegmentVisualBuilder
              key="builder"
              conditionGroups={parsedConditionGroups ?? []}
              onChange={groups => handleContentsChange(JSON.stringify(groups, null, 2))}
              onUnsupported={showUnsupportedSegmentInJsonEditor}
            />
          </SegmentSchemaCatalogProvider>
        ) : editorMode === 'preview' ? (
          <SegmentDescriptionView conditionGroups={parsedConditionGroups ?? []} />
        ) : (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={handleContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames={false}
            hideHeader
            notice={jsonEditorNotice ?? undefined}
          />
        )}
      </article>

      <RecipientsPreview
        conditions={formatType === 'legacy' ? parsedConditions : null}
        conditionGroups={formatType === 'new' ? conditionGroupsForPreview : null}
        isJsonValid={isJsonValid}
      />
    </section>
  );
}
